import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { logError } from "../lib/log";

const STORAGE_KEY = "chemlab_tutor_model";

export const FALLBACK_MODELS = [
  { key: "sonnet-5", provider: "anthropic", label: "Claude Sonnet 5", blurb: "Balanced — best all-rounder" },
  { key: "opus-4-8", provider: "anthropic", label: "Claude Opus 4.8", blurb: "Deepest reasoning, slower" },
  { key: "haiku-4-5", provider: "anthropic", label: "Claude Haiku 4.5", blurb: "Fastest replies" },
  { key: "gpt-5-4", provider: "openai", label: "GPT-5.4", blurb: "ChatGPT all-rounder" },
  { key: "gpt-5-4-mini", provider: "openai", label: "GPT-5.4 Mini", blurb: "ChatGPT, fast & light" },
];

export const PROVIDER_LABEL = { anthropic: "Claude", openai: "ChatGPT" };

export function useAiModels() {
  const [models, setModels] = useState(FALLBACK_MODELS);
  useEffect(() => {
    api.get("/ai/models").then((r) => setModels(r.data.models)).catch((e) => logError("Model list failed", e));
  }, []);
  return models;
}

export function useTutorModel() {
  const models = useAiModels();
  const [model, setModelState] = useState(() => localStorage.getItem(STORAGE_KEY) || "sonnet-5");
  const setModel = (k) => { localStorage.setItem(STORAGE_KEY, k); setModelState(k); };
  const current = models.find((m) => m.key === model) || models[0];
  return { models, model, setModel, current };
}

const PROVIDER_STYLE = {
  anthropic: { on: "border-fuchsia-400/60 bg-fuchsia-500/15 text-fuchsia-100", hover: "hover:border-fuchsia-400/40 hover:text-fuchsia-200", tag: "text-fuchsia-300" },
  openai: { on: "border-emerald-400/60 bg-emerald-500/15 text-emerald-100", hover: "hover:border-emerald-400/40 hover:text-emerald-200", tag: "text-emerald-300" },
};

export function ModelPicker({ models, model, onChange, disabled }) {
  const providers = [...new Set(models.map((m) => m.provider))];
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-4" data-testid="model-picker" role="radiogroup" aria-label="AI model">
      {providers.map((p) => (
        <div key={p} className="flex flex-wrap items-center gap-2" data-testid={`model-group-${p}`}>
          <span className={`text-[10px] uppercase tracking-widest ${PROVIDER_STYLE[p]?.tag || "text-slate-400"}`}>{PROVIDER_LABEL[p] || p}</span>
          {models.filter((m) => m.provider === p).map((m) => {
            const active = m.key === model;
            const st = PROVIDER_STYLE[p] || PROVIDER_STYLE.anthropic;
            return (
              <button
                key={m.key}
                type="button"
                role="radio"
                aria-checked={active}
                disabled={disabled}
                onClick={() => onChange(m.key)}
                data-testid={`model-${m.key}`}
                title={m.blurb}
                className={`rounded-full border px-3 py-1.5 text-xs transition-colors disabled:opacity-50 ${active ? st.on : `border-white/10 bg-white/5 text-slate-300 ${st.hover}`}`}
              >
                <span className="font-semibold">{m.label}</span>
                <span className="hidden lg:inline text-slate-400"> · {m.blurb}</span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
